import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'

import CreatePracticeSessionDialog from '../../../components/presenter/practice-with-simba/CreatePracticeSession'
import SessionsFilterAndSortBar from '../../../components/ai-mentor/practice-mode/SessionsFilterAndSortBar'
import { getTimePassedFrom, getTransformedGroupDetails, Icons } from '../../../utils/utilities'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import CustomTooltip from '../../../components/common/CustomTooltip'
import lazySimba from '../../../assets/simba-ai/celebration.png'
import simbaImg from '../../../assets/simba-ai/celebration.png'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import { Link, useSearchParams } from 'react-router-dom'
import { useUserStore } from '../../../store/user.store'
import PracticeSession from './PracticeSession'

// Driver for Create Steps
import { practiceSessionCreateDrive } from '../../../utils/driverjs-drives'
import CustomChip from '../../../components/common/CustomChip'
import SimbaDetailsDailog from '../../../components/docs-and-support/SimbaDetailsDialog'
import LeaderboardDialog from '../../../components/ai-mentor/LeaderboardDialog'


const tabs = [
    { label: "Practice Sessions", value: "practice-sessions", icon: Icons.default.QuizModule },
    { label: "Reference Materials", value: "materials", icon: Icons.default.File },
]

const PracticeWithSimba = observer( () => {

    const [selectedPracticeSession, setSelectedPracticeSession] = useState( null )
    const [loadingPracticeSessions, setLoadingPracticeSessions] = useState( true )
    const [leaderboardDialogOpen, setLeaderboardDialogOpen] = useState( false )
    const [showSimbaAiDetails, setShowSimbaAiDetails] = useState( false )
    const [filterOptions, setFilterOptions] = useState( [] )
    // const [tabValue, setTabValue] = useState( 'practice-sessions' )
    const [createMode, setCreateMode] = useState( false )
    const [userRole, setUserRole] = useState( null )
    const [loading, setLoading] = useState( true )
    const [subject, setSubject] = useState( null )

    const { palette, layout, shadows, border } = useTheme()

    const UserStore = useUserStore()
    const SimbaAIStore = useSimbaAIStore()

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const sort = searchParams.get( 'sort' )
    const filters = searchParams.getAll( "filter" ).map( item => {
        const obj = Object.fromEntries(
            item.split( "," ).map( p => p.split( ":" ) )
        )
        return { field: obj.field, value: obj.value }
    } )

    const fetchError = SimbaAIStore.getErrors.practiceSessions !== null && SimbaAIStore.getErrors.practiceSessions

    const removeFilters = () => {
        setSearchParams( prev => {
            const newValues = new URLSearchParams( prev )
            newValues.delete( 'searchText' )
            newValues.delete( 'filter' )
            return newValues
        } )
    }

    const practiceSessions = useMemo( () => {
        let data = [...SimbaAIStore.getPracticeSessions]

        // 1. Global search
        if ( searchText.trim() ) {
            const q = searchText.trim().toLowerCase()
            data = data.filter( ps => ( ps.title?.toLowerCase().includes( q ) || ps.access_groups?.some( ag => ag.title?.toLowerCase().includes( q ) ) ) )
        }

        // 2. Per-column filters
        data = data.filter( row => {

            const filterHandler = ( { field, value } ) => {

                if ( !value ) return true // no filter
                const practiceSessionValue = ( field === "subject" ? row?.subject?.subject_code : row?.subject?.ac_year || "" )?.toLowerCase()
                const filterValue = String( value ).toLowerCase()

                console.log( practiceSessionValue, filterValue )

                return practiceSessionValue === filterValue
            }

            return filters.every( filterHandler )
        } )

        // 3. Multi-column sort
        const sortArray = sort
        const multiplier = ["oldestfirst", "alphaasc"].includes( sort ) ? 1 : -1
        if ( sort )
            data.sort( ( a, b ) => {
                const va = ["oldestfirst", "latestfirst"].includes( sort ) ? a.created_at : a.title
                const vb = ["oldestfirst", "latestfirst"].includes( sort ) ? b.created_at : b.title

                const sa = String( va ).toLowerCase()
                const sb = String( vb ).toLowerCase()

                if ( sa < sb ) return -1 * multiplier
                if ( sa > sb ) return 1 * multiplier
                return 0
            } )

        return data
    }, [filters, sort, searchText, SimbaAIStore.getPracticeSessions] )

    const getData = useCallback( async ( refresh = false ) => {
        setLoading( true )

        if ( UserStore.getErrorsStatuses?.subjects === true || refresh )
            await UserStore.fetchUserSubjects( refresh )
        try {
            setUserRole( UserStore.getUser.user_role )

            const subjectsDetails = UserStore.getUserSubjectsDetails
            if ( UserStore.getUser.user_role === 'STUDENT' ) {
                if ( subjectsDetails.yearWise ) {
                    setFilterOptions( subjectsDetails.years )
                } else {
                    setFilterOptions( subjectsDetails.semesters )
                }
            } else {
                let subject = localStorage.getItem( 'practiceWithSimbaSubject' )
                let subjectFilter = localStorage.getItem( 'practiceWithSimbaSubjectFilter' )

                if ( !subject )
                    subject = UserStore.getUserSubjectsDetails.currentSubjects?.find( sub => sub.ac_year === subjectsDetails.academicYears[0] )
                else
                    subject = JSON.parse( subject )

                const acYears = subjectsDetails.academicYears?.sort( ( a, b ) => a > b ? -1 : 1 )

                if ( !subjectFilter )
                    subjectFilter = acYears[0]
                else
                    subjectFilter = subjectFilter

                setFilterOptions( acYears )
                setSubject( subject )
            }

        } catch ( err ) {
            console.log( err )
        } finally {
            setLoading( false )
        }
    }, [UserStore] )

    const getPracticeSessions = useCallback( async () => {
        setLoadingPracticeSessions( true )
        await SimbaAIStore.fetchPracticeSessions()
        setLoadingPracticeSessions( false )
        setLoading( false )
    }, [SimbaAIStore] )


    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getPracticeSessions()
        // getReferenceMaterials()
    }, [getPracticeSessions] )


    // const referenceMaterials = SimbaAIStore.getReferenceMaterials.filter( ps => ps?.subject?.subject_code === subject?.subject_code )

    return (
        <Box padding={layout.pagePadding} display="flex" overflow="auto" flexDirection="column" flexGrow={1}>

            <SimbaDetailsDailog open={showSimbaAiDetails} onClose={() => setShowSimbaAiDetails( false )} />
            <LeaderboardDialog open={leaderboardDialogOpen} setOpen={setLeaderboardDialogOpen} />

            <Box flexGrow={1} display="flex" flexDirection="column" gap={layout.sectionsGap}>
                <Box display="flex" flexDirection="column" gap={layout.cardsGap}>
                    <Box display="flex" flexDirection={{ xs: "column", md: "row" }} flexWrap="wrap" gap={layout.cardsGap} justifyContent="space-between" alignItems={{ md: "flex-end", xs: "flex-start" }}>
                        <Box position="relative"  >
                            <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Practice Sessions with Simba</Typography>
                            <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >The Acharya AI Learning Initiative.</Typography>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={layout.cardsGap} alignItems="center">
                            {/* <Button variant='outlined' onClick={() => setLeaderboardDialogOpen( true )} color='greyed' size='small' startIcon={Icons.default.AnalyticsIcon} >
                                Leaderboard
                            </Button> */}
                            <Button variant='outlined' onClick={() => practiceSessionCreateDrive.drive()} color='greyed' size='small' startIcon={Icons.default.LiveHelpIcon} >
                                How to create
                            </Button>
                            <Button onClick={() => setShowSimbaAiDetails( true )} variant='outlined' color='greyed' size='small' startIcon={Icons.default.Info} >
                                Learn about practice sessions
                            </Button>
                        </Box>
                    </Box>

                </Box>
                {!loading && subject && UserStore.getErrorsStatuses?.subjects === false && <Box display="flex" flexGrow={1} flexDirection="column" gap={layout.cardsGap}>


                    <Box onClick={() => setLeaderboardDialogOpen( true )} alignItems="center" display="flex" gap={layout.cardsGap} sx={{ cursor: "pointer", background: `linear-gradient(135deg, ${palette.customThemeColor.light + 44}, transparent)`, ":hover": { borderColor: palette.primary.main } }} border={border[1]} borderRadius={layout.borderRadius} padding="10px 10px">


                        <Box component="img" src={simbaImg} height="50px" />
                        <Box flexGrow={1}>
                            {/* <Typography lineHeight={1.3} variant='h6' fontSize="16px">Simba AI is here!</Typography>
                                            <Typography variant='subtitle2' fontSize="12px">Practice the concepts learnt in your classes with the help of Simba AI.</Typography> */}
                            <Typography lineHeight={1.3} variant='h6' fontSize="16px">Simba AI Monthly Leaderboard</Typography>
                            <Typography variant='subtitle2' fontSize="12px">Check who is leading the preactice race in your department!</Typography>
                        </Box>
                        <Box display="flex" >
                            <Typography position="relative" zIndex={5} right="-15px" bottom="-10px" fontSize="2rem" color='silver' >{Icons.default.EmojiEvents}</Typography>
                            <Typography position="relative" zIndex={10} fontSize="2.5rem" color='gold'>{Icons.default.EmojiEvents}</Typography>
                            <Typography position="relative" zIndex={5} left="-15px" bottom="-10px" fontSize="2rem" color='brown' >{Icons.default.EmojiEvents}</Typography>
                        </Box>
                        <IconButton size='small'>
                            {Icons.default.ChevronRightIcon}
                        </IconButton>
                    </Box>

                    <SessionsFilterAndSortBar />

                    {/* Practice Sessions Created */}
                    {/* {tabValue === "practice-sessions" && */}
                    <Box>

                        {!fetchError && subject && <CreatePracticeSessionDialog buttonLabel='Create Practice Session' variant="fab" key={subject.subject_code} subject={subject} open={createMode} onClose={() => setCreateMode( false )} />}

                        {!fetchError && !loadingPracticeSessions && practiceSessions.length > 0 && <Box display="grid" alignItems="center" flexWrap="wrap" gap="10px" gridTemplateColumns={{ md: "repeat(4, minmax(250px, 1fr))", xs: "1fr" }} >
                            {practiceSessions.map( practiceSession => {
                                const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()

                                return (
                                    <Link style={{ flexGrow: 1 }} key={practiceSession._id} className='default-link' to={`/faculty/practice-with-simba/${practiceSession._id}`}>
                                        <Box flexGrow="1" bgcolor={palette.contentBg} sx={{ cursor: "pointer", ":hover": { boxShadow: shadows[1], borderColor: palette.text.secondary } }} padding="10px" borderRadius="5px" border={border[1]} key={practiceSession.id}>
                                            <Box color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                                <CustomChip variant='filled' textVariant="subtitle2" textStyles={{ fontSize: "10px" }} color={practiceSession.status === "draft" ? "error" : "success"} value={practiceSession.status?.toUpperCase()} />
                                                <CustomChip color="primary" textVariant="subtitle2" textStyles={{ fontSize: "10px" }} value={`Created ${getTimePassedFrom( practiceSession.created_at )}`} />
                                            </Box>
                                            <Tooltip disableInteractive title={practiceSession.title}>
                                                <Typography variant='h6' noWrap component="p" fontSize="16px" >{practiceSession.title}</Typography>
                                            </Tooltip>
                                            <Typography display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"   > {Icons.default.Subjects} {practiceSession.subject.subject_name}</Typography>
                                            {/* <Typography display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"   > {Icons.default.Calendar} {formatDate( practiceSession.date_range_start )} to {formatDate( practiceSession.date_range_end )}</Typography> */}
                                            <Tooltip disableInteractive title={topics.join( ", " )}>
                                                <Typography display="block" noWrap alignItems="center" gap="5px" fontSize="12px"  > {Icons.default.Category} {topics.slice( 0, 2 ).join( ", " )}{topics.length > 2 ? `, +${topics.length - 2} More.` : ""}</Typography>
                                            </Tooltip>
                                            <Box color={palette.text.primary} display="flex" gap="5px" fontSize="16px" alignItems="center">
                                                {Icons.default.Group}
                                                <CustomTooltip disableInteractive title={getTransformedGroupDetails( practiceSession.access_groups[0] ).short_title} >
                                                    <Typography variant='subtitle2' padding="2px 5px" color='textSecondary' bgcolor={palette.contentBg} borderRadius="10px" border={`1px solid ${palette.common.font}`} noWrap fontSize="10px" >
                                                        {getTransformedGroupDetails( practiceSession.access_groups[0] ).short_title}
                                                    </Typography>
                                                </CustomTooltip>
                                                {practiceSession.access_groups.length > 1 && <CustomTooltip disableInteractive title={practiceSession.access_groups.slice( 1 ).map( ag => getTransformedGroupDetails( ag ).short_title ).join( ", " )}>
                                                    <Typography variant='subtitle2' padding="2px 5px" color='textSecondary' bgcolor={palette.contentBg} borderRadius="10px" border={`1px solid ${palette.common.font}`} noWrap fontSize="10px" >+{practiceSession.access_groups.length - 1} more</Typography>
                                                </CustomTooltip>}
                                            </Box>
                                        </Box>
                                    </Link>
                                )
                            } )}
                        </Box>}
                        {!fetchError && !loadingPracticeSessions && subject && UserStore.getErrorsStatuses?.subjects === false && practiceSessions.length === 0 && <Box display="flex" flexDirection="column" gap="20px" alignItems="center" padding="20px" borderRadius={layout.borderRadius} justifyContent="space-between" bgcolor={palette.contentBg}>
                            <Box component="img" src={lazySimba} height="200px" />
                            {( filters.length > 0 || searchText ) && <Typography variant='subtitle2' color='textSecondary' component="p" >No practice sessions found for the filters!</Typography>}
                            {!( filters.length > 0 || searchText ) && <Typography variant='subtitle2' color='textSecondary' component="p" >No practice sessions created yet!</Typography>}
                            <Box display="flex" gap={layout.cardsGap} flexWrap="wrap">
                                <CreatePracticeSessionDialog startIcon={Icons.default.AddIcon} variant='button' size="small" disableElevation />
                                {( filters.length > 0 || searchText ) && <Button onClick={removeFilters} variant='contained' disableElevation color='error' startIcon={Icons.default.FilterAltOff} size='small'>Remove filters</Button>}
                            </Box>
                        </Box>}

                        {fetchError && <Box display="flex" flexDirection="column" gap="20px" alignItems="center" padding="20px" borderRadius={layout.borderRadius} justifyContent="space-between" bgcolor={palette.contentBg}>
                            <TryAgainMessageBlock loading={loadingPracticeSessions} err={SimbaAIStore.getErrors.practiceSessions?.message} code={SimbaAIStore.getErrors.practiceSessions?.code} getData={getPracticeSessions} />
                        </Box>}
                    </Box>
                    {/* } */}

                    {/* Reference Materials */}
                    {/* {tabValue === "materials" && <Box position="relative" flexGrow={1}>

                        {!SimbaAIStore.getErrors.referenceMaterials && <ReferenceMaterialUploadDialog subject={subject} attachments={uploadingAttachments} setAttachments={setUploadingAttachments} open={uploadingAttachments.length > 0} onClose={() => setUploadingAttachments( [] )} />}

                        {!SimbaAIStore.getErrors.referenceMaterials && !loadingReferenceMaterials && referenceMaterials.length > 0 && <CustomTable
                            containerStyles={{
                                flexGrow: "1"
                            }}
                            globalSearch
                            columns={referenceMaterialsTableColumns}
                            data={referenceMaterials}
                        />}

                        {!SimbaAIStore.getErrors.referenceMaterials && !loadingReferenceMaterials && subject && UserStore.getErrorsStatuses?.subjects === false && referenceMaterials.length === 0 && <Box display="flex" flexDirection="column" gap="20px" alignItems="center" padding="20px" borderRadius={layout.borderRadius} justifyContent="space-between" bgcolor={palette.contentBg}>
                            <Box component="img" src={lazySimba} width="200px" />
                            <Typography variant='subtitle2' color='textSecondary' >No practice sessions created yet!</Typography>
                            <Box position="relative">
                                <Button onClick={() => refUploadInputRef.current?.click()} variant='contained' disableElevation size="small" startIcon={Icons.default.AddIcon}>
                                    Upload Reference Materials
                                </Button>
                            </Box>
                        </Box>}

                        {SimbaAIStore.getErrors.referenceMaterials && <Box display="flex" flexDirection="column" gap="20px" alignItems="center" padding="20px" borderRadius={layout.borderRadius} justifyContent="space-between" bgcolor={palette.contentBg}>
                            <TryAgainMessageBlock loading={loadingReferenceMaterials} err={SimbaAIStore.getErrors.referenceMaterials?.message} code={SimbaAIStore.getErrors.referenceMaterials?.code} getData={getReferenceMaterials} />
                        </Box>}

                    </Box>} */}
                </Box>}

                {selectedPracticeSession && <PracticeSession practiceSession={selectedPracticeSession} onClose={() => setSelectedPracticeSession( null )} open={Boolean( selectedPracticeSession )} />}

                {!loading && UserStore.getErrorsStatuses?.subjects === true && <TryAgainMessageBlock loading={loading} styles={{ margin: "20px" }} parent='component' getData={() => getData()} />}
                {loading && UserStore.getErrorsStatuses?.subjects === false && <DataLoadingSpinner padding="0" waitingMessage="Loading subject please wait..." />}
                {/* </Paper > */}
            </Box>
        </Box >
    )
} )

export default PracticeWithSimba




{/* Filters 
{/* <Box display="flex" flexWrap="wrap" gap={layout.cardsGap}>
                        {!loading && <Select
                            sx={{ flexGrow: { xs: 1, md: "initial" }, minWidth: "0" }}
                            size='small'
                            renderValue={( value ) => `${UserStore.getUser.user_role === "STUDENT" ? UserStore.getUserSubjectsDetails.yearWise ? "Year" : "Semester" : "Academic Year"} ${value}`}
                            value={filter}
                            onChange={event => handleSubFilterChange( event.target.value )}
                        >
                            {filterOptions.map( ( sem, i ) => (
                                <MenuItem dense sx={{ fontSize: "14px" }} value={sem} key={sem} >
                                    {UserStore.getUser.user_role === "STUDENT" ? UserStore.getUserSubjectsDetails.yearWise ? "Year" : "Semester" : "Academic Year"} {sem}
                                </MenuItem>
                            ) )}
                        </Select>}
                        {!loading && <Select
                            sx={{ flexGrow: { xs: 1, md: "initial" }, minWidth: "0" }}
                            size='small'
                            renderValue={( value ) => value ? `${subject.subject_name} (${subject.subject_code})` : "Select subject"}
                            value={subject ? subject.subject_code : ""}
                            displayEmpty
                            onChange={handleSubjectChange}
                        >
                            <MenuItem value="" disabled key="default" sx={{ fontSize: "14px" }} >Select subject</MenuItem>
                            {UserStore.getUserSubjectsDetails.currentSubjects
                                .filter( subjectsFilterFunc ).map( ( sub, i ) => (
                                    <MenuItem dense selected={sub.subject_code === ( subject?.subject_code || "" )} sx={{ fontSize: "14px", width: "min-content" }} value={sub.subject_code} key={sub.subject_code} >{sub.subject_name} ({sub.subject_code})</MenuItem>
                                ) )}
                        </Select>}
                    </Box> 

{/* {subject && <Box display="flex" alignItems="flex-end" justifyContent="space-between" padding="10px" borderRadius="5px" bgcolor={palette.customThemeColor.light + "22"} border={`1px solid ${palette.customThemeColor.light}`}>
                        <Box >
                            <Typography component="p" fontSize="14px" fontWeight="600" color="customThemeColor.dark" >{subject.subject_name}</Typography>

                            <Box accept='.pdf,application/pdf' onChange={onFileSelect} ref={refUploadInputRef} overflow="hidden" position="absolute" maxHeight="0" maxWidth="0" component="input" type="file" multiple />

                            <Box marginTop="5px" display="flex" gap="10px" alignItems="center" flexWrap="wrap">
                                <Typography fontSize="10px" padding="5px 10px" borderRadius="5px" color={palette.customThemeColor.contrastText} bgcolor={palette.customThemeColor.main} fontWeight="500">{subject.subject_code}</Typography>
                                <Typography fontSize="10px" padding="5px 10px" borderRadius="5px" color={palette.customThemeColor.contrastText} bgcolor={palette.customThemeColor.main} fontWeight="500">{subject.ac_year}</Typography>
                                <Typography fontSize="10px" padding="5px 10px" borderRadius="5px" color={palette.customThemeColor.contrastText} bgcolor={palette.customThemeColor.main} fontWeight="500">{practiceSessions.length} Practice sessions</Typography>
                            </Box>
                        </Box>
                    </Box>} 

{/* <Box color={palette.text.secondary} marginTop="5px" display="flex" gap="5px" fontSize="14px" alignItems="center">
                                                {topics.slice( 0, 2 ).map( ( topic, index ) => (
                                                    <Typography title={topic} maxWidth="100px" noWrap fontSize="12px" key={topic} borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 10px" >{topic}</Typography>
                                                ) )}
                                                {topics.length > 2 && <Typography fontSize="12px" borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 10px" >+{topics.length - 2} More</Typography>}
                                            </Box> */}